<template>
  <div>
    <v-overlay v-model="loading" class="align-center justify-center">
      <v-card color="primary" dark width="300">
        <v-card-text>
          Loading...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-overlay>
    <v-form v-model="valid" autocomplete="off">
      <v-container>
        <v-row v-if="updatedCommunity">
          <v-col cols="12" sm="10">
            <v-alert dismissible type="success" class="success-alert">
              User replaced successfully. To check your changes for <strong>{{ updatedCommunity.name }}</strong> please
              <a :href="updatedCommunity.url">click here</a>
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <span class="screenHeading">User Replacement (Config)</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="10">
            <v-autocomplete
              :items="communities"
              item-value="communityAbbreviation"
              item-title="communityName"
              variant="underlined"
              label="Select community"
              return-object
              clearable
              required
              :rules="selectedCommunityRules"
              v-model="selectedCommunity"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="fullCommunity.id">
          <v-col cols="12" sm="10">
            <v-autocomplete
              :items="peopleInCommunity"
              label="People available for replacement/removal"
              variant="underlined"
              return-object
              clearable
              item-title="displayName"
              item-value="displayName"
              :rules="selectedUserToReplaceRules"
              v-model="selectedUserToReplace"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="selectedUserToReplace">
          <v-col cols="12" sm="10">
            <people-picker
              v-model="selectedNewUser"
              :user="selectedNewUser"
              label="Replace with (default)"
              @clear="selectedNewUser = null"
              :errorMessages="newUserErrorMessages"
            ></people-picker>
          </v-col>
        </v-row>
        <v-row dense v-if="displayPopulateNewUser" class="py-0">
          <v-col cols="12" sm="10" class="py-0">
            <a @click.prevent="populateNewUser()" class="populate-new-user-link">Set new user in all ocurrences</a>
          </v-col>
        </v-row>
        <v-row v-if="!ocurrencesExist">
          <v-col cols="12" sm="10">
            <v-btn :color="!this.selectedUserToReplace ? '' : 'success'" :disabled="!this.selectedUserToReplace" type="button" @click="showOcurrences">Find user</v-btn>
          </v-col>
        </v-row>
        <v-row v-if="ocurrencesExist">
          <v-col cols="12" sm="10">
            <v-card>
              <v-card-title>Selected user found in following configuration</v-card-title>
              <v-list>
                <v-list-item-group>
                  <template v-for="(value, propertyName) in ocurrences" :key="propertyName">
                    <div>
                      <h4 class="px-3">{{ configurationDisplayNames[propertyName] }}</h4>
                      <v-list-item dense selectable :min-width="200" v-for="(ocurrence, i) in value" :key="i" :ripple="false">
                        <v-row justify="center" align="center">
                          <v-col cols="3" v-if="ocurrence.role !== 'facilitator'">
                            <v-list-item-action>
                              <v-checkbox
                                color="red"
                                class="item-checkbox"
                                label="Remove user"
                                v-model="ocurrence.isRemoving"
                                @change="onItemCheckboxChange($event, ocurrence)"
                              ></v-checkbox>
                            </v-list-item-action>
                          </v-col>

                          <v-col :cols="ocurrence.role !== 'facilitator' ? '9' : '12'">
                            <v-row justify="center" align="center">
                              <v-col cols="12" :sm="ocurrence.isRemoving ? '12' : '7'" class="py-0">
                                <v-list-item-title>
                                  {{ ocurrence.user.displayName }} as
                                  <strong>{{ configurationDisplayNames[ocurrence.role] }}</strong>
                                </v-list-item-title>
                                <v-list-item-subtitle class="list-subtitle" v-if="ocurrence.name"
                                  >Found in: {{ ocurrence.name }}</v-list-item-subtitle
                                >
                                <v-list-item-subtitle class="list-subtitle" v-else>
                                  <div v-for="(label, index) in relationshipSummary[ocurrence.entityId]" :key="index">
                                    Found in: {{ label }}
                                    <span v-if="index + 1 !== relationshipSummary[ocurrence.entityId].length">&nbsp;&</span>
                                  </div>
                                </v-list-item-subtitle>
                              </v-col>
                              <v-col cols="12" sm="5" class="py-0" v-if="!ocurrence.isRemoving">
                                <people-picker
                                  v-model="ocurrence.newUser"
                                  :user="ocurrence.newUser"
                                  @clear="clearNewUser(ocurrence)"
                                  :errorMessages="ocurrencesNewUserErrorMessages(ocurrence.newUser)"
                                ></people-picker>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-list-item>
                    </div>
                  </template>
                </v-list-item-group>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="selectedUserToReplace && ocurrencesExist">
          <v-col cols="12" sm="10">
            <v-btn color="success" :disabled="!valid" type="button" @click="showConfirmationDialog = true">Apply changes</v-btn>
            <v-btn type="button" class="ml-3" @click="showCancelDialog = true">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <v-dialog persistent v-model="showConfirmationDialog" max-width="500">
      <v-card v-if="showConfirmationDialog">
        <v-container>
          <h3>Are you sure you want to proceed with these changes ?</h3>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="red" text @click="replaceUser">Yes</v-btn>
            <v-btn color="black" text @click="showConfirmationDialog = false">No</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="showCancelDialog" max-width="500">
      <v-card v-if="showCancelDialog">
        <v-container>
          <h3>Are you sure you want to cancel ?</h3>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="red" text @click="cancelChanges">Yes</v-btn>
            <v-btn color="black" text @click="showCancelDialog = false">No</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
    <toaster ref="toaster"></toaster>
  </div>
</template>

<script>
import MOCAdminService from '@/services/admin.service'
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'
import Toaster from '@/components/Toaster'
import userReplacementMixins from '@/mixins/userReplacementMixins'

export default {
  mixins: [userReplacementMixins],
  components: {
    PeoplePicker,
    Toaster
  },
  data() {
    return {
      fullCommunity: {},
      selectedCommunity: null,
      selectedUserToReplace: null,
      selectedNewUser: null,
      communities: [],
      loading: false,
      ocurrences: {},
      valid: false,
      updatedCommunity: null,
      showConfirmationDialog: false,
      showCancelDialog: false,
      allQuestionsAnswers: [],
      relationshipSummary: {},
      selectedCommunityRules: [(newValue) => (!newValue ? 'This field is required' : true)],
      selectedUserToReplaceRules: [(newValue) => (!newValue ? 'This field is required' : true)],
      isRemovingUser: false
    }
  },
  computed: {
    // TODO: what happens if someone updates the community before the user finishes it's replacement ???

    peopleInCommunity() {
      return this.getPeopleInCommunity(this.fullCommunity)
    },
    newUserErrorMessages() {
      return this.selectedUserToReplace &&
        this.selectedNewUser &&
        this.selectedUserToReplace.id === this.selectedNewUser.id &&
        this.selectedUserToReplace.displayName === this.selectedNewUser.displayName
        ? 'New user cannot be the same as current user'
        : null
    },
    ocurrencesExist() {
      return Object.keys(this.ocurrences).length > 0
    },
    displayPopulateNewUser() {
      return this.selectedNewUser && this.ocurrencesExist
    }
  },
  methods: {
    onItemCheckboxChange(value, item) {
      if (value) {
        item.newUser = undefined
      }
    },
    clearNewUser(ocurrence) {
      ocurrence.newUser = undefined
    },
    getQA() {
      this.allQuestionsAnswers = (this.fullCommunity.questions || []).reduce((acc, cur) => {
        acc[cur.id] = cur
        const result = this.getQuestionsFromAnswers(cur, cur.values, cur)
        acc = {
          ...acc,
          ...result
        }
        return acc
      }, [])
    },
    getQuestionsFromAnswers(root = {}, values = [], parentQuestion = {}, questionHierarchy = [], answerHierarchy = []) {
      return (values || []).reduce((a, c) => {
        for (let cur of c.questions) {
          answerHierarchy = answerHierarchy
            .filter((a) => a.parentQuestionClientId !== parentQuestion.id)
            .concat({
              label: c.label,
              id: c.id,
              parentQuestionClientId: parentQuestion.id
            })

          questionHierarchy = questionHierarchy
            .filter((c) => c.id !== parentQuestion.id)
            .concat({ label: parentQuestion.label, id: parentQuestion.id })

          cur.questionHierarchy = questionHierarchy
          cur.answerHierarchy = answerHierarchy
          a[cur.id] = cur
          a = {
            ...a,
            ...this.getQuestionsFromAnswers(root, cur.values, cur, questionHierarchy, answerHierarchy)
          }
        }
        return a
      }, {})
    },
    getLabel(question) {
      if (!(question.questionHierarchy || []).length) return question.label
      const hierarchyLength = (question.questionHierarchy || []).length + (question.answerHierarchy || []).length
      let list = []
      for (let i = 0; i < hierarchyLength; i++) {
        if (question.questionHierarchy[i]) list.push(question.questionHierarchy[i].label)
        if (question.answerHierarchy[i]) list.push(question.answerHierarchy[i].label)
      }

      list.push(question.label)

      return list.join(' / ')
    },
    loadRelationshipSummary() {
      this.relationshipSummary = (this.fullCommunity.qaRelationships || []).reduce((acc, cur) => {
        if (!acc[cur.id]) acc[cur.id] = []
        cur.criteria.forEach((c) => {
          const question = this.allQuestionsAnswers[c.questionId] || {}
          const value = question.values.find((v) => v.id === c.valueId) || {}
          acc[cur.id].push(`${this.getLabel(question) || ''} - ${value.label || ''}`)
        })
        return acc
      }, {})
    },
    populateNewUser() {
      this.loading = true
      Object.keys(this.ocurrences).forEach((k) => {
        this.ocurrences[k].forEach((o) => {
          if (o.isRemoving) return
          o.newUser = JSON.parse(JSON.stringify(this.selectedNewUser))
        })
      })
      this.loading = false
    },
    ocurrencesNewUserErrorMessages(user) {
      return user && user.id === this.selectedUserToReplace.id && user.displayName === this.selectedUserToReplace.displayName
        ? 'New user cannot be the same as current user'
        : null
    },
    cancelChanges() {
      this.selectedUserToReplace = null
      this.selectedNewUser = null
      this.ocurrences = {}
      this.fullCommunity = {}
      this.selectedCommunity = null
      this.updatedCommunity = null
      this.showCancelDialog = false
    },
    loadSelectedCommunity(communityAbbreviation) {
      if (this.loading) return
      this.loading = true
      MOCAdminService.getCommunity(communityAbbreviation)
        .then((res) => (this.fullCommunity = res))
        .then(this.getQA)
        .then(this.loadRelationshipSummary)
        .catch((err) => {
          switch (err.response.status) {
            case 401:
              this.$refs.toaster.pop('error', 'You are unauthorized to view this community')
              break
            default:
              this.$refs.toaster.pop('error', 'Failed to retrieve data')
          }
        })
        .finally(() => (this.loading = false))
    },
    showOcurrences() {
      this.loading = true
      this.ocurrences = this.authorizedRemovedUserOcurrencesInConfig = this.findUserInCommunity(
        this.fullCommunity,
        this.selectedUserToReplace
      )

      if (this.selectedNewUser && this.selectedNewUser.id) {
        this.populateNewUser()
      }
      this.loading = false
    },
    replaceUser() {
      const ocurrences = Object.keys(this.ocurrences).reduce((acc, key) => {
        const config = this.ocurrences[key]
        const parsedConfig = config
          .filter((c) => c.isRemoving || c.newUser)
          .map((c) => ({
            entityName: key,
            entityId: c.entityId,
            oldUser: c.user,
            newUser: c.newUser,
            roleName: c.role,
            isRemoving: c.isRemoving
          }))
        acc = acc.concat(parsedConfig)
        return acc
      }, [])

      if (!ocurrences.length) {
        this.$refs.toaster.pop('error', 'Please select at least one role to replace')
        return
      }
      if (this.loading) return
      this.showConfirmationDialog = false
      this.loading = true
      MOCAdminService.replaceUser({
        communityAbbreviation: this.fullCommunity.communityAbbreviation,
        ocurrences
      })
        .then(() => {
          this.updatedCommunity = {
            url: `/${this.selectedCommunity.communityAbbreviation}/CommunityConfig/`,
            name: `${this.selectedCommunity.communityName}`
          }
          this.selectedUserToReplace = null
          this.selectedNewUser = null
          this.ocurrences = {}
          this.fullCommunity = {}
          this.selectedCommunity = null
          this.$refs.toaster.pop('success', 'User replaced successfully')
        })
        .catch((err) => {
          this.$refs.toaster.pop('error', err.response.data.message ? err.response.data.message : 'User replacement failed')
        })
        .finally(() => {
          this.loading = false
        })
    },
    getPeoplePickerInjectedProperties() {
      return {
        confidentialOptions: {
          confidentialCommunityAbbreviation: this.selectedCommunity.isConfidential
            ? this.fullCommunity.communityAbbreviation
            : undefined
        }
      }
    }
  },
  created() {
    if (this.loading) return
    this.loading = true
    MOCAdminService.getUserCommunities()
      .then(
        (succ) =>
          (this.communities = [...succ.sort((a, b) => (a.communityName.toUpperCase() > b.communityName.toUpperCase() ? 1 : -1))])
      )
      .catch(() => {})
      .finally(() => (this.loading = false))
  },
  watch: {
    selectedCommunity(newValue) {
      this.selectedUserToReplace = null
      this.selectedNewUser = null
      this.ocurrences = {}
      this.fullCommunity = {}
      if (!newValue) return newValue
      this.loadSelectedCommunity(newValue.communityAbbreviation)
    },
    selectedUserToReplace() {
      this.ocurrences = {}
    }
  },
  provide() {
    return { getPeoplePickerInjectedProperties: this.getPeoplePickerInjectedProperties }
  }
}
</script>
<style scoped>
.success-alert a {
  color: white;
  font-weight: bold;
}

.content-end {
  justify-content: flex-end;
  display: flex;
}

.populate-new-user-link {
  position: relative;
  top: -5px;
  text-decoration: underline;
}

.list-subtitle {
  max-width: 556px;
  text-wrap: wrap;
}
</style>
